// ════════════════════════════════════════════════════════════
//  SkyControls.jsx — the Sky Box's buttons (inside the Front Row bar)
// ════════════════════════════════════════════════════════════
//  One row of verbs (sit/stand, hold your sign, wave, cheer), a small sign editor
//  behind ✎, and who's in the box — a name opens their Mark (THE GLANCE LAW).
//  Never a wall of buttons: the rest is one tap away (caP'n: never drown them).
//  Keys (only while you're in the box, never while typing): WASD/arrows walk,
//  E sit/stand, F sign, 1 wave, 2 cheer. They live here so the 3D never owns a
//  key listener; the body reads BOX.keys.
//  PUBLIC / PRIVATE (caP'n 2026-10-18): the switch on the people row. Public = the
//  shared box strangers land in; Private = yours, friends by invite (＋ Friends).
// ════════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import { BOX, useBox, toggleSit, toggleSignUp, setSign, setBoard, setBody, emote, isGuest, boxMoveTo, people, setBoxMode, inPublic } from "../online/skybox.js";
import { SIGN, SIGN_BOARDS, EMOTES, BODIES, BOX_MODES } from "../data/skybox.js";
import { say } from "../data/text.js";

const MOVE = new Set(["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"]);
const typing = (e) => { const t = e.target; return !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable); };

export default function SkyControls() {
  useBox();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(BOX.me.sign);
  const m = BOX.me, seated = m.seat >= 0;

  useEffect(() => {
    const down = (e) => {
      if (typing(e) || e.ctrlKey || e.metaKey || e.altKey || BOX.ui.modal) return;   // a Mark / the friends panel is open: the box's keys stand down
      const k = e.key.toLowerCase();
      if (MOVE.has(k)) { e.preventDefault(); BOX.keys.add(k); return; }
      if (e.repeat) return;
      if (k === "e") toggleSit();
      else if (k === "f") { e.preventDefault(); signUp(); }   // preventDefault: when F opens the editor, the "f" must not land in the box
      else { const em = EMOTES.find((x) => x.key === k); if (em) emote(em.id); }
    };
    const up = (e) => BOX.keys.delete(e.key.toLowerCase());
    const blur = () => BOX.keys.clear();
    // a text box taking focus mid-walk (chat, the sign) swallows the key-up — let go of every key right then
    const focus = (e) => { if (typing(e)) BOX.keys.clear(); };
    window.addEventListener("keydown", down); window.addEventListener("keyup", up); window.addEventListener("blur", blur); window.addEventListener("focusin", focus);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); window.removeEventListener("blur", blur); window.removeEventListener("focusin", focus); BOX.keys.clear(); };
  }, []);

  function signUp() {
    if (!BOX.me.signUp && !BOX.me.sign) { setEditing(true); return; }   // nothing to hold up yet: open the editor instead
    toggleSignUp();
  }
  const saveSign = () => { setSign(draft); setEditing(false); toggleSignUp(!!draft.trim()); };   // an emptied sign goes down

  const who = people(), owner = isGuest() ? (BOX.folk[BOX.hostUid] || BOX.host) : null;
  const pub = inPublic(), whose = owner ? say(pub ? "sky.theirs.public" : "sky.theirs", { name: owner.n || owner.name || "" })
    : isGuest() ? say("sky.friendsbox") : say(pub ? "sky.public" : BOX.mode === "private" ? "sky.private" : "sky.yours");
  const host = BOX.host;
  return (
    <div className="sky-ctl">
      <div className="sky-row">
        <button className="sky-main" onClick={toggleSit} title="E">{say(seated ? "sky.stand" : "sky.sit")}</button>
        <button className={m.signUp ? "on" : ""} onClick={signUp} title="F">{say(m.signUp ? "sky.sign.down" : "sky.sign.up")}</button>
        <button className={editing ? "on" : ""} onClick={() => { setDraft(BOX.me.sign); setEditing((v) => !v); }} title={say("sky.sign.write")}>✎ {say("sky.sign.edit")}</button>
        {EMOTES.map((em) => <button key={em.id} onClick={() => emote(em.id)} title={em.key}>{say("sky.emote." + em.id)}</button>)}
      </div>
      {editing && (
        <div className="sky-row sky-sign">
          <input autoFocus value={draft} maxLength={SIGN.max} placeholder={say("sky.sign.write")}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") saveSign(); else if (e.key === "Escape") { e.currentTarget.blur(); setEditing(false); } }}
            onKeyUp={(e) => e.stopPropagation()} />
          <span className="sky-count">{SIGN.max - draft.length}</span>
          {SIGN_BOARDS.filter((b) => b.free).map((b) => (
            <button key={b.id} className={"sky-board" + (m.board === b.id ? " on" : "")} style={{ background: b.colors[0], borderColor: b.colors[2] }} onClick={() => setBoard(b.id)} aria-label={b.id} />
          ))}
          <button className="sky-main" onClick={saveSign}>✓</button>
        </div>
      )}
      <div className="sky-row sky-people">
        <span className="sky-whose">{whose}</span>
        {BOX.match && BOX.writable && (
          <span className="sky-mode" title={say("sky.mode.hint")}>
            {BOX_MODES.map((id) => <button key={id} className={!isGuest() && BOX.mode === id ? "on" : ""} onClick={() => setBoxMode(id)}>{say("sky.mode." + id)}</button>)}
          </span>
        )}
        {who.map((p) => (
          <button key={p.uid} className={"sky-chip" + (p.me ? " me" : "")} onClick={() => BOX.ui.openMark && BOX.uid && BOX.ui.openMark(p.me ? BOX.uid : p.uid)}>{p.n}</button>
        ))}
        {who.length < 2 && BOX.match && <span className="sky-alone">{say(pub ? "sky.alone.public" : "sky.alone")}</span>}
        {BOX.ui.openFriends && BOX.ui.friendsReady && <button className="sky-chip add" onClick={() => BOX.ui.openFriends()}>＋ {say("sky.friends")}</button>}
        <span className="sky-body">
          {Object.entries(BODIES).map(([id, b]) => <button key={id} className={m.body === id ? "on" : ""} onClick={() => setBody(id)}>{b.name}</button>)}
        </span>
      </div>
      {isGuest() && host && host.match && (host.match !== BOX.match || (!host.f && host.box && host.box !== BOX.box)) && (
        <div className="sky-row sky-moved">
          {say(host.match !== BOX.match ? "sky.moved" : "sky.movedbox", { name: host.name || "" })}
          <button className="sky-main" onClick={() => BOX.ui.follow && BOX.ui.follow(host)}>{say("sky.follow")}</button>
          <button onClick={() => boxMoveTo(null)}>{say("sky.home")}</button>
        </div>
      )}
      {!BOX.match && <div className="fr-hint">{say("sky.offline")}</div>}
      <div className="fr-hint">{say(seated ? "sky.hint.seated" : "sky.hint")}</div>
    </div>
  );
}
